import { withHTMLComponent } from '@updater-legacy/components/custom-element';
import preventScrolling from './prevent-scrolling';
import '@updater-legacy/components/focus-out';
import '@updater-legacy/components/focus-trap';
import styles from './index.css';

const ESCAPE_KEY = 27;

const html = `
  <upd-focus-out>
    <upd-focus-trap>
      <div class="backdrop"></div>
      <div class="content">
        <slot></slot>
      </div>
    </upd-focus-trap>
  </upd-focus-out>
`;

export default class UIDialog extends withHTMLComponent(styles, html)() {
  static get observedAttributes() {
    return ['open', 'parent-height'];
  }

  constructor() {
    super();

    this.returnValue = '';
    this.focustrap = this.shadowRoot.querySelector('upd-focus-trap');
  }

  focusOutHandler = () => {
    // While this dosen't seem necessary in firefox clicking on the backdrop will fire this event thus closing modals
    // without it.
    if (!this.hasAttribute('modal')) {
      this.close();
    }
  };

  keydownHandler = (e) => {
    e.keyCode === ESCAPE_KEY && this.close();
  };

  onBackdropClick = () => {
    this.dispatchEvent(
      new CustomEvent('backdropclick', {
        bubbles: true,
        composed: true,
        detail: {
          open: this.hasAttribute('open'),
        },
      })
    );
  };

  attributeChangedCallback(attr, oldVal, newVal) {
    if (attr == 'parent-height') {
      this.style.setProperty(
        '--ui-dialog-parent-height',
        this.getAttribute('parent-height')
      );
    }

    if (attr === 'open') {
      this.hasAttribute('open') ? this.setupHandlers() : this.cleanupAndEmit();
    }
  }

  connected() {
    this.setupHandlers();
    this.setAttribute('tabindex', '-1');
  }

  setupHandlers() {
    if (!this.backdrop) {
      this.backdrop = this.shadowRoot.querySelector('.backdrop');
    }

    this.addEventListener('updfocusout', this.focusOutHandler);
    this.addEventListener('keydown', this.keydownHandler);
    this.backdrop.addEventListener('click', this.onBackdropClick);
  }

  cleanupHandlers() {
    this.removeEventListener('updfocusout', this.focusOutHandler);
    this.removeEventListener('keydown', this.keydownHandler);
    this.backdrop &&
      this.backdrop.removeEventListener('click', this.onBackdropClick);
  }

  cleanupAndEmit() {
    this.cleanupHandlers();

    if (this.hasAttribute('modal')) {
      this.cleanupModal();
    }

    this.emitClose();
  }

  emitClose() {
    this.dispatchEvent(new Event('close'));
  }

  show() {
    if (!this.hasAttribute('open')) {
      this.setAttribute('open', '');
    }
  }

  showModal() {
    if (!this.hasAttribute('open')) {
      this.restoreScrolling = preventScrolling();
      this.focustrap.isDisabled = false;
      this.setAttribute('modal', '');
      this.setAttribute('open', '');

      // focus after everything is visible onscreen
      this.focustrap.focus();
    }
  }

  cleanupModal() {
    this.restoreScrolling && this.restoreScrolling();
    this.restoreScrolling = null;
    this.focustrap.isDisabled = true;
    this.removeAttribute('modal');
  }

  close(returnValue) {
    if (!this.hasAttribute('open')) {
      return;
    }

    this.removeAttribute('open');

    if (returnValue) {
      this.returnValue = returnValue;
    }

    return this.returnValue;
  }

  disconnected() {
    this.close();
  }
}

window.customElements.define('upd-dialog', UIDialog);
